import { httpClient as axiosInstance, apiUrls } from "../api"
import { getApiErrorInfo } from "@/src/utils/api-error"
import { toast } from "@/src/common/snackbar"

import { normalizeManagedPromptPolicies } from "@/src/features/internalUsers/utils/promptPolicyUtils"
import type {
    PromptPoliciesResponse,
    PromptPolicy,
    PromptPolicyMutationResponse,
    PromptPolicyVersionList,
    PromptPolicyVersionListResponse,
    UpdatePromptPolicyPayload,
    WorkspacePromptPolicyOverrideList,
    WorkspacePromptPolicyOverrideListResponse,
} from "@/src/types/promptPolicy"

function showErrorToast(error: unknown, fallback: string) {
    const { message } = getApiErrorInfo(error, fallback)
    toast.error(message)
}

export const fetchPromptPolicies = async (): Promise<Record<string, PromptPolicy>> => {
    try {
        const response = await axiosInstance.get<PromptPoliciesResponse>(apiUrls.ADMIN_PROMPT_POLICIES)
        return normalizeManagedPromptPolicies(response.data.data)
    } catch (error) {
        console.error("Error fetching prompt policies:", error)
        showErrorToast(error, "Failed to fetch prompt policies")
        throw error
    }
}

export const fetchPromptPolicyByName = async (
    policyName: string,
    globalPolicies?: Record<string, PromptPolicy>
): Promise<string> => {
    if (globalPolicies) {
        const matchedPolicy = Object.values(globalPolicies).find((policy) => policy.name === policyName)
        return matchedPolicy?.config?.prompt ?? ""
    }
    try {
        const policies = await fetchPromptPolicies()
        const matchedPolicy = Object.values(policies).find((policy) => policy.name === policyName)
        return matchedPolicy?.config?.prompt ?? ""
    } catch (error) {
        console.error("Error fetching prompt policy by name:", error)
        showErrorToast(error, "Failed to fetch prompt")
        throw error
    }
}

export const fetchWorkspaceOverrides = async (policyName: string): Promise<WorkspacePromptPolicyOverrideList> => {
    try {
        const response = await axiosInstance.get<WorkspacePromptPolicyOverrideListResponse>(
            apiUrls.ADMIN_PROMPT_POLICY_WORKSPACE_OVERRIDES(policyName)
        )
        return response.data.data
    } catch (error) {
        console.error("Error fetching workspace prompt overrides:", error)
        showErrorToast(error, "Failed to fetch workspace overrides")
        throw error
    }
}

export const fetchPromptForWorkspaceEdit = async (
    policyName: string,
    workspaceId: string,
    globalPolicies: Record<string, PromptPolicy>
): Promise<string> => {
    try {
        const { overrides } = await fetchWorkspaceOverrides(policyName)
        const override = overrides.find((row) => row.workspaceId === workspaceId)
        if (override?.config?.prompt) {
            return override.config.prompt
        }
        return fetchPromptPolicyByName(policyName, globalPolicies)
    } catch (error) {
        console.error("Error loading prompt for workspace edit:", error)
        throw error
    }
}

export const updatePromptPolicy = async (
    policyName: string,
    payload: UpdatePromptPolicyPayload
): Promise<PromptPolicy> => {
    try {
        const response = await axiosInstance.put<PromptPolicyMutationResponse>(
            apiUrls.ADMIN_UPDATE_PROMPT_POLICY(policyName),
            payload
        )
        return response.data.data
    } catch (error) {
        console.error("Error updating prompt policy:", error)
        showErrorToast(error, "Failed to update prompt policy")
        throw error
    }
}

export type ApplyPromptPolicyGloballyPayload = {
    config: UpdatePromptPolicyPayload["config"]
    description?: string
    changeComment: string
}

export const applyPromptPolicyGlobally = async (
    policyName: string,
    payload: ApplyPromptPolicyGloballyPayload
): Promise<PromptPolicy> => {
    try {
        const response = await axiosInstance.post<PromptPolicyMutationResponse>(
            apiUrls.ADMIN_PROMPT_POLICY_APPLY_GLOBAL(policyName),
            payload
        )
        return response.data.data
    } catch (error) {
        console.error("Error applying prompt policy globally:", error)
        showErrorToast(error, "Failed to apply prompt for all workspaces")
        throw error
    }
}

export const fetchPromptPolicyVersions = async (policyName: string): Promise<PromptPolicyVersionList> => {
    try {
        const response = await axiosInstance.get<PromptPolicyVersionListResponse>(
            apiUrls.ADMIN_PROMPT_POLICY_VERSIONS(policyName)
        )
        return response.data.data
    } catch (error) {
        console.error("Error fetching prompt policy versions:", error)
        showErrorToast(error, "Failed to fetch version history")
        throw error
    }
}

export const deleteWorkspacePromptOverride = async (
    policyName: string,
    workspaceId: string
): Promise<void> => {
    try {
        await axiosInstance.delete(apiUrls.ADMIN_PROMPT_POLICY_DELETE_WORKSPACE_OVERRIDE(policyName, workspaceId))
    } catch (error) {
        console.error("Error deleting workspace prompt override:", error)
        showErrorToast(error, "Failed to reset workspace to global prompt")
        throw error
    }
}

export const revertPromptPolicyVersion = async (
    policyName: string,
    versionNumber: number
): Promise<PromptPolicy> => {
    try {
        const response = await axiosInstance.post<PromptPolicyMutationResponse>(
            apiUrls.ADMIN_PROMPT_POLICY_REVERT(policyName, versionNumber)
        )
        return response.data.data
    } catch (error) {
        console.error("Error reverting prompt policy version:", error)
        showErrorToast(error, "Failed to revert prompt version")
        throw error
    }
}
